import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UserCheck, FolderPlus, LayoutGrid, Calendar, FileText, Users, Briefcase,
  ArrowLeft, ShieldCheck, Sparkles, Check, Award, Gift, BadgePercent,
} from 'lucide-react';
import PublicCoachHeader from './PublicCoachHeader';
import PublicCoachFooter from './PublicCoachFooter';
import Reveal from '../components/landing/Reveal';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '../styles/publicTheme.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_MOCKUP = 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/7374293879a06b9bd0ae82ed624d18ee0d388ca2695769ff99d61e63c5d553c5.jpeg';

const JOURNEY_STEPS = [
  { icon: UserCheck, name: 'الملف المهني', desc: 'بياناتك وخبراتك ورخصك والفئات العمرية المستهدفة' },
  { icon: FolderPlus, name: 'مكتبة التمارين', desc: 'مكتبتك الخاصة لحفظ وتصنيف تمارينك وتكتيكاتك' },
  { icon: LayoutGrid, name: 'الاستديو التكتيكي', desc: 'تصميم التمارين على ملعب تفاعلي متكامل' },
  { icon: Calendar, name: 'الخطط والجداول', desc: 'تجميع التمارين في خطط أسبوعية ووحدات تدريبية' },
  { icon: FileText, name: 'التقارير الفنية', desc: 'تقارير وتقييمات قابلة للمراجعة والتصدير' },
  { icon: Users, name: 'طاقم العمل', desc: 'مساحة عمل جماعية لإدارة الطاقم الفني وتوزيع المهام' },
  { icon: Briefcase, name: 'العروض والتعاقدات', desc: 'استقبال طلبات التواصل وعروض العمل من المنشآت' },
];

const SHOWCASES = [
  {
    id: 'profile',
    badge: 'الملف المهني الرقمي',
    title: 'ملفك المهني وسيرتك الذاتية الموثقة',
    desc: 'سجّل خبراتك التدريبية والشهادات والمؤهلات والرخص المعتمدة، والفئات العمرية التي تبرع فيها. تحكم في مستوى الظهور (خاص، للمنشآت المسجلة، أو الدليل العام) مع إمكانية تصدير سيرتك الذاتية بنقرة واحدة.',
    bullets: ['توثيق الشهادات والرخص الفنية', 'تحكم كامل بخصوصية الظهور والبيانات الحساسة', 'تصدير سيرة ذاتية احترافية بصيغة PDF'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/f58fa94ef2f75c8dceef9d145bb48bab08a41ac267f6e18cef900f45b5b56184.jpeg',
  },
  {
    id: 'studio',
    badge: 'الاستديو الفني والملعب التكتيكي',
    title: 'صمّم تمارينك على ملعب تفاعلي احترافي',
    desc: 'أدوات رسم وتكتيك متكاملة: إضافة اللاعبين، الأقماع، السهام الحركية، والمناطق المحددة. حدّد الشدة والزمن وعدد اللاعبين والأدوات المطلوبة والمراحل التدريبية بدقة.',
    bullets: ['عناصر تكتيكية تفاعلية (لاعبون، أقماع، سهام ومناطق)', 'تحديد دقيق للزمن والشدة والأدوات والمراحل', 'حفظ التمارين وتعديلها بسهولة'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/50397564e27d490f65e25e89739cc4d50b51a80b99e110f3b8a269a3c6eb2bc1.jpeg',
  },
  {
    id: 'plans',
    badge: 'الخطط والجداول التدريبية',
    title: 'حوّل تمارينك إلى خطط وجداول أسبوعية',
    desc: 'نظّم وحداتك التدريبية حسب الفريق والفئة العمرية والهدف الفني. جدول أسبوعي متكامل يسهّل متابعة الجاهزية وتنفيذ البرامج التدريبية.',
    bullets: ['ربط التمارين مباشرة بالجدول الأسبوعي', 'تصنيف حسب الفئات والأهداف الرياضية', 'سهولة المتابعة والتحضير للمباريات'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/52ec4f25e4fa61f1dfcfb058d69038f99f0b98ec0d993c8de598f40db80d09c6.jpeg',
  },
  {
    id: 'library',
    badge: 'المكتبة والتقارير الفنية',
    title: 'مكتبة تمارين شخصية وتقارير فنية دقيقة',
    desc: 'احفظ كل تمارينك وتكتيكاتك في مكتبتك الخاصة مع فلترة وبحث سريع. أصدر تقارير فنية للمباريات والتمارين جاهزة للمراجعة مع الإدارة أو الطاقم.',
    bullets: ['بحث وتصنيف ذكي للتمارين والتكتيكات', 'مشاركة سريعة ومحكومة بالصلاحيات', 'تقارير تقييم فني وتكتيكي قابلة للتصدير'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/a845cf090d2c49d58d5368ced7289af7f638bf5f6477cb2250efb161ef7294b3.jpeg',
  },
  {
    id: 'crew',
    badge: 'طاقم العمل الفني',
    live: true,
    title: 'إدارة طاقم العمل التدريبي والمشاركة الفعالة',
    desc: 'ميزة تعمل فعلياً الآن: أنشئ طاقمك المستقل، وادعُ مدربين آخرين (مسجلين أو جدد)، وشارك التمارين والخطط التدريبية وفق صلاحيات محددة بدقة.',
    bullets: ['ميزة حقيقية تعمل الآن في النظام', 'دعوة مدربين مسجلين أو جدد بمرونة', 'مشاركة التمارين والخطط حسب الصلاحيات'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/f684622f5fda1fc8e90ade64f4a62a542c5ac50232a18ec4e467e56b75277444.jpeg',
  },
  {
    id: 'directory',
    badge: 'الدليل العام والعروض',
    title: 'حضور أمام المنشآت وعروض عمل منظمة',
    desc: 'اظهر في دليل المدربين الخاص بالمنشآت والأندية الباحثة عن كفاءات. استقبل طلبات التواصل وعروض التعاقد الرسمية مع حماية كاملة لبياناتك الحساسة ورقم جوالك حتى موافقتك الصريحة.',
    bullets: ['ظهور موجه للأندية والأكاديميات الموثقة', 'إدارة الطلبات والعروض في مكان واحد', 'حماية رقم الجوال والبيانات الشخصية'],
    img: 'https://static.prod-images.emergentagent.com/jobs/609b32fa-fcbc-4c66-8ec2-6410ad7f99a9/images/1d05c24755a586c70f1997744226267a7ec8b7d287244f07f094b1fb6b4ad38c.jpeg',
  },
];

const EDGE_POINTS = [
  { title: 'الملكية الكاملة للمحتوى', desc: 'تمارينك وخططك تظل بحسابك الشخصي دائماً' },
  { title: 'الربط السلس مع المنشآت', desc: 'ربط مساحة عملك بالأكاديمية أو النادي فور التعاقد' },
  { title: 'الاستقلالية والاستمرارية', desc: 'انتقالك بين الأندية لا يعني البدء من الصفر' },
];

export default function CoachesLandingPage() {
  const navigate = useNavigate();
  const [coachesCount, setCoachesCount] = useState(null);
  const [plan, setPlan] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const { theme, toggleTheme } = usePublicTheme();

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/saas/public-stats`).then((r) => setCoachesCount(r.data?.active_coaches)).catch(() => {});
    axios.get(`${API_BASE_URL}/api/coaches/plan/public`).then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const goRegister = () => navigate('/coaches/register');

  return (
    <div className="coaches-landing-root min-h-screen bg-[#060f1c] text-white" dir="rtl" data-testid="coaches-landing-page" data-pub-theme={theme}>
      <PublicCoachHeader title="تسيار للمدربين" dark={theme === 'dark'} theme={theme} onToggleTheme={toggleTheme} />

      {/* الهيرو */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 bg-[#060f1c]">
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage: 'linear-gradient(rgba(148,163,184,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.14) 1px, transparent 1px)',
              backgroundSize: '46px 46px',
              maskImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, black 40%, transparent 100%)',
            }}
          />
          <div className="absolute -top-32 -end-32 h-[28rem] w-[28rem] rounded-full bg-teal-500/10 blur-3xl" />
          <div className="absolute -bottom-16 -start-16 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-2">
          <Reveal>
            <span className="font-display inline-block rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-1.5 text-xs font-bold tracking-wide text-teal-400" data-testid="coaches-landing-badge">
              تسيار للمدربين — منظومة متخصصة ضمن منصة تسيار الرياضية
            </span>
            <h1 className="font-display mt-6 text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="coaches-landing-title">
              مساحة عمل المدرب… من الفكرة إلى الملعب
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="coaches-landing-subtitle">
              أنشئ ملفك المهني، صمّم تمارينك وخططك، نظّم طاقمك، وكن حاضرًا أمام المنشآت الرياضية المناسبة — في منظومة واحدة.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <button
                onClick={goRegister}
                className="group flex items-center justify-center gap-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 px-8 py-4 text-base font-bold text-white shadow-[0_0_30px_rgba(245,76,7,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(245,76,7,0.55)]"
                data-testid="coach-hero-register-btn"
              >
                ابدأ كمدرب
                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/coaches/directory')}
                className="flex items-center justify-center rounded-full border border-white/20 px-8 py-4 text-base font-bold text-white transition-colors hover:border-white/50 hover:bg-white/10"
                data-testid="coach-hero-directory-btn"
              >
                استعرض دليل المدربين
              </button>
            </div>
            {typeof coachesCount === 'number' && coachesCount > 0 && (
              <p className="mt-8 text-sm text-slate-400" data-testid="coaches-landing-stat">
                <span className="font-display text-xl font-black text-teal-400">+{coachesCount}</span> مدرب نشط بالفعل عبر منصة تسيار
              </p>
            )}
          </Reveal>
          <Reveal delay={100}>
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-teal-500/20 via-transparent to-orange-500/10 blur-2xl" />
              <img
                src={HERO_MOCKUP}
                alt="واجهات تسيار للمدربين: الملف المهني، الملعب التكتيكي، التمارين والخطط"
                className="relative z-10 w-full rounded-[1.5rem] border border-white/10 object-cover shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
                data-testid="coach-hero-mockup-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* مسار المنتج */}
      <section className="border-y border-teal-900/20 bg-[#0A192F] py-20" data-testid="coach-journey-section">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-wider text-teal-400">مسار متصل</span>
            <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl">كل رحلة المدرب في مكان واحد</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            {JOURNEY_STEPS.map((s, i) => (
              <Reveal key={s.name} delay={i * 50}>
                <div className="group flex h-full flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-center transition-all hover:-translate-y-1 hover:border-teal-500/50" data-testid={`coach-journey-step-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400 transition-colors group-hover:bg-teal-500/20">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xs font-bold text-white sm:text-sm">{s.name}</h3>
                  <p className="hidden text-[11px] leading-relaxed text-slate-400 sm:block">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* معرض المزايا */}
      <section className="bg-[#060f1c] py-8" data-testid="coach-showcases-section">
        {SHOWCASES.map((s, i) => (
          <div key={s.id} className={`py-16 ${i % 2 === 1 ? 'bg-[#0A192F]/50' : 'bg-[#060f1c]'}`}>
            <div className="mx-auto max-w-6xl px-5">
              <div className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-2 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <Reveal>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-teal-500/10 blur-2xl" />
                    <img src={s.img} alt={s.title} className="relative z-10 w-full rounded-2xl border border-white/10 object-cover shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]" data-testid={`coach-showcase-image-${s.id}`} />
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-500/20 bg-teal-500/10 px-3 py-1 text-xs font-bold text-teal-400" data-testid={`coach-showcase-badge-${s.id}`}>
                    {s.live && <Sparkles className="h-3 w-3" />}
                    {s.badge}
                    {s.live && <span className="text-teal-300">· تعمل الآن</span>}
                  </span>
                  <h3 className="font-display mt-4 text-xl font-bold text-white sm:text-2xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{s.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* الميزة التنافسية */}
      <section className="border-y border-teal-900/20 bg-[#0A192F] py-20" data-testid="coach-edge-section">
        <div className="mx-auto max-w-5xl px-5 text-center">
          <Reveal>
            <ShieldCheck className="mx-auto h-9 w-9 text-teal-400" />
            <h2 className="font-display mt-4 text-2xl font-bold text-white sm:text-3xl">مدرب مستقل أو ضمن منشأة — أنت تملك مساحة عملك</h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              ملفك المهني ومكتبة تمارينك وخططك وطاقمك ملك لك كمدرب، تستمر معك حتى بلا منشأة. عند التعاقد، تربط عملك بالمنشأة بمرونة دون أن تفقد ملكية محتواك.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {EDGE_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 70}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-start" data-testid={`coach-edge-point-${i}`}>
                  <h4 className="font-display text-base font-bold text-white">{p.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* باقة المدرب المحترف */}
      {plan && (
        <section className="bg-[#060f1c] py-24" data-testid="coach-pricing-section">
          <div className="mx-auto max-w-3xl px-5">
            <Reveal className="text-center">
              <span className="text-sm font-bold uppercase tracking-wider text-teal-400">تسعير واضح ومستقل للمدربين</span>
              <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl">{plan.name || 'باقة المدرب المحترف'}</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400 sm:text-base">{plan.tagline}</p>
            </Reveal>

            <Reveal delay={80}>
              <div className="mt-10 rounded-3xl border border-teal-500/20 bg-gradient-to-b from-white/[0.04] to-transparent p-8" data-testid="coach-pricing-card">
                <div className="flex items-center justify-center gap-1 rounded-full border border-white/10 bg-white/5 p-1 mx-auto w-fit">
                  <button onClick={() => setBilling('monthly')} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${billing === 'monthly' ? 'bg-teal-500 text-[#060f1c]' : 'text-slate-400'}`} data-testid="coach-pricing-toggle-monthly">شهري</button>
                  <button onClick={() => setBilling('yearly')} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${billing === 'yearly' ? 'bg-teal-500 text-[#060f1c]' : 'text-slate-400'}`} data-testid="coach-pricing-toggle-yearly">سنوي</button>
                </div>

                <div className="mt-6 text-center">
                  {(billing === 'monthly' ? plan.monthly_price_original : plan.yearly_price_original) != null && (
                    <span className="ml-2 text-lg font-semibold text-slate-500 line-through" data-testid="coach-pricing-original-amount">
                      {billing === 'monthly' ? plan.monthly_price_original : plan.yearly_price_original}
                    </span>
                  )}
                  <span className="font-display text-5xl font-black text-white" data-testid="coach-pricing-amount">
                    {billing === 'monthly' ? plan.monthly_price : plan.yearly_price}
                  </span>
                  <span className="mr-1 text-sm text-slate-400">{plan.currency || 'SAR'} / {billing === 'monthly' ? 'شهرياً' : 'سنوياً'}</span>
                </div>

                {plan.discount_active && plan.discount_percent > 0 && (
                  <div className="mx-auto mt-3 flex w-fit items-center gap-1.5 rounded-full bg-orange-500/15 px-3 py-1.5 text-xs font-bold text-orange-300" data-testid="coach-pricing-discount-badge">
                    <BadgePercent className="h-3.5 w-3.5" /> {plan.discount_label || 'عرض خاص'} — خصم {plan.discount_percent}%
                  </div>
                )}
                {plan.free_trial_days > 0 && (
                  <div className="mx-auto mt-2 flex w-fit items-center gap-1.5 rounded-full bg-teal-500/15 px-3 py-1.5 text-xs font-bold text-teal-300" data-testid="coach-pricing-trial-badge">
                    <Gift className="h-3.5 w-3.5" /> أول {plan.free_trial_days} يوماً مجاناً بلا أي بطاقة دفع
                  </div>
                )}

                <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(plan.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300" data-testid="coach-pricing-feature-item">
                      <Award className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={goRegister}
                  className="group mx-auto mt-9 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 px-8 py-4 text-base font-bold text-white shadow-[0_0_30px_rgba(245,76,7,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(245,76,7,0.55)] sm:w-auto"
                  data-testid="coach-pricing-join-btn"
                >
                  ابدأ الآن / انضم كشريك مؤسس
                  <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* الختام */}
      <section className="relative overflow-hidden bg-[#0A192F] py-24" data-testid="coaches-landing-cta">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #0d9488 0, transparent 45%), radial-gradient(circle at 80% 70%, #f54c07 0, transparent 45%)' }} />
        <Reveal className="relative z-10 mx-auto max-w-3xl px-5 text-center">
          <h2 className="font-display text-2xl font-black text-white sm:text-3xl">ابنِ مسيرتك التدريبية باحتراف</h2>
          <p className="mt-4 text-slate-300">تسيار للمدربين — منظومة متخصصة ضمن منصة تسيار الرياضية</p>
          <div className="mx-auto mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <button
              onClick={goRegister}
              className="group flex items-center justify-center gap-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 px-8 py-4 text-base font-bold text-white shadow-[0_0_30px_rgba(245,76,7,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(245,76,7,0.55)]"
              data-testid="coach-final-cta-register-btn"
            >
              أنشئ ملفك المهني الآن
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            </button>
            <button
              onClick={() => navigate('/coaches/directory')}
              className="group mx-auto flex items-center justify-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-8 py-4 text-base font-bold text-teal-300 transition-all hover:border-teal-400/60 hover:bg-teal-500/20"
              data-testid="coach-landing-directory-btn"
            >
              استعراض دليل المدربين
            </button>
          </div>
        </Reveal>
      </section>

      <PublicCoachFooter />
    </div>
  );
}
